import { useMutation, useQueryClient } from '@tanstack/react-query';

import { hasKey } from '~/utils';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { Application, PatchApplicationAgencyResponse } from '@ab/ams-school-api/types/private/v1';

export type PatchBodyAgencySelected = {
  agencyExternalId: string;
  agencyName: string;
  institutionSupport?: boolean;
};

export function usePatchAgency(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { isLoading, error, mutateAsync } = useMutation(
    ['application', id],
    async (payload: PatchBodyAgencySelected) => {
      const response = await request<PatchApplicationAgencyResponse>(
        `${config.apiHost}/private/v1/applications/${id}/agency`,
        {
          method: 'PATCH',
          body: {
            data: {
              id: id,
              type: 'application',
              attributes: {
                agencyName: payload.agencyName,
                agencyExternalId: payload.agencyExternalId,
                institutionSupport: payload.institutionSupport,
              },
            },
          },
          isExpectedResponse,
        }
      );

      const app = response.data;
      const attrs = app.attributes;

      queryClient.invalidateQueries(['application', id]);

      return {
        ...attrs,
        id: app.id,
      } as Application;
    }
  );

  return {
    patchAgency: mutateAsync,
    patchAgencyError: error,
    isPatchingAgency: isLoading,
  };
}
function isExpectedResponse(res: unknown): res is PatchApplicationAgencyResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
